import { useCallback, useEffect, useState } from 'preact/hooks';
import { api } from './api.ts';
import { subscribe, type ConnectionState } from './realtime.ts';
import { apply, fromSnapshot, type ScreenState } from './screen.ts';
import type { EventDetail, VotingView } from './types.ts';

/**
 * Estado en vivo de una sesión para staff: detalle por HTTP, avisos por WebSocket y resincronía
 * al reconectar (o polling sin WebSocket).
 */
export function useEventScreen(eventId: string) {
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [state, setState] = useState<ScreenState | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('connecting');
  const [error, setError] = useState<string | null>(null);

  const sync = useCallback(async () => {
    try {
      const d = await api<EventDetail>('GET', `/api/events/${eventId}`);
      // La abierta o, si no hay, la última: se sigue mostrando su resultado.
      const votingId = d.openVotingId ?? d.lastVotingId;
      const voting = votingId ? (await api<{ voting: VotingView }>('GET', `/api/votings/${votingId}`)).voting : null;
      setDetail(d);
      setState(fromSnapshot(d, voting));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [eventId]);

  useEffect(() => {
    void sync();
    return subscribe(eventId, {
      onSync: () => void sync(),
      onState: setConnection,
      onMessage: (m) => setState((s) => (s ? apply(s, m) : s)),
    });
  }, [eventId, sync]);

  return { detail, state, connection, error, setError, sync };
}
